import { useEffect, useState } from 'react';
import { track } from 'analytics';
import { BodyContainer, CardContainer, ImageContainer, Text } from './styles';
import fbTokenExpired from "assets/images/fb-token-expired.png"
import FB, { loginWithFB } from 'helpers/FB';
import { ReactComponent as QuestionMarkIcon } from "assets/images/question-mark.svg";
import { SymphonyTooltip } from 'modules/Sty';
import Intercom from 'helpers/Intercom';
import useScreen from 'Hooks/useScreen';
import ButtonSymphony from 'elements/ButtonSymphony';
import useBrand from 'Hooks/useBrand';
import { getCampaign_api } from 'api/campaignApi';
import { connectToBrand_api } from 'api/brandApi';
import { useFacebookConnections } from 'Hooks/useFacebookConnections';

interface Props {
  onSuccess?: () => void,
  campaignId: number,
  campaignType: string,
}

const MAINTENANCE_MODE = false

/** Configuration for different modes */
const CONTENT_CONFIG = {
  maintenance: {
    title: "Facebook's Ads servers are having issues",
    description: "Sorry for the inconvenience, but Facebook's Ads servers are having issues pulling data at the moment. The Symphony team is working with Facebook to resolve, but while this is being solved you can view campaign stats at the link below:",
    buttonText: "View on Facebook",
    footer: "We should have this resolved shortly. Your data is safe and secure and you'll see your dashboard soon."
  },
  normal: {
    title: "Your Facebook session has expired",
    description: "Please login to your Facebook account to reconnect Symphony to your Facebook. (You'll only have do this once)",
    buttonText: "Login to Facebook"
  }
} as const;

const FbTokenExpired = ({
  onSuccess,
  campaignId,
  campaignType,
}: Props) => {
  const { mobileView } = useScreen();
  const { currentBrand, getBrand } = useBrand()
  const { getFacebookAuthToken } = useFacebookConnections();

  const [loading, setLoading] = useState<boolean>(false)

  const updateInBrand = async () => {
    if (!currentBrand) {
      return
    }

    try {
      const accessToken = await getFacebookAuthToken();
    
      if (accessToken) {
        await connectToBrand_api({
          brandId: currentBrand.id,
          brandSlug: currentBrand.slug,
          service: 'logged_in_fb_user',
          value: {
            logged_in_fb_user: accessToken,
          }
        })
        onSuccess?.()
        track('User reconnected FB', {
          brand_id: currentBrand.id,
          brand_name: currentBrand.name,
          campaign_id: campaignId,
          campaign_type: campaignType,
        })
      }
    } catch (error) {
      track('Error reconnecting Facebook', {
        brand_id: currentBrand.id,
        brand_name: currentBrand.name,
        campaign_id: campaignId,
        campaign_type: campaignType,
        error,
      })
    }
  }

  const onClickLogin = async () => {
    setLoading(true);
    await updateInBrand();
    await getBrand();
    setLoading(false);
  }

  const [gettingCampaignUrl, setGettingCampaignUrl] = useState(true)
  const [campaignUrl, setCampaignUrl] = useState('');

  const openIntercom = () => Intercom.openFacebookSessionArticle();

  /** Renders the button based on the current mode */
  const renderButton = () => {
    if (MAINTENANCE_MODE) {
      return (
        <ButtonSymphony
          className="fbTokenExpiredViewButton"
          variant="outlined"
          disabled={loading}
          isLoading={!campaignUrl || gettingCampaignUrl}
          onClick={() => campaignUrl && window.open(campaignUrl, '_blank')}
        >
          View on Facebook
        </ButtonSymphony>
      );
    }

    return (
      <ButtonSymphony
        className="buttonFBTokenExpired"
        onClick={onClickLogin}
        disabled={loading}
        isLoading={loading}
        variant="outlined"
      >
        Login to Facebook
      </ButtonSymphony>
    );
  };

  /** Common layout component for both modes */
  const renderContent = () => {
    const content = MAINTENANCE_MODE ? CONTENT_CONFIG.maintenance : CONTENT_CONFIG.normal;

    return (
      <CardContainer paddingY={mobileView ? 32 : 120}>
        <ImageContainer width={mobileView ? "56%" : "40%"}>
          <img src={fbTokenExpired} alt="facebook-token-expired" />
        </ImageContainer>
        <BodyContainer>
          <Text fontSize={20} color="#000000" textAlign="center">
            {content.title}
            <button onClick={openIntercom} className="cursor">
              <SymphonyTooltip
                className="ml-2 w-6 h-6"
                title="Click here to learn more."
                arrow
              >
                <QuestionMarkIcon height={24} width={24} />
              </SymphonyTooltip>
            </button>
          </Text>
          <Text fontSize={18} color="#707070" textAlign="center">
            {content.description}
          </Text>
          {renderButton()}
          {MAINTENANCE_MODE && (
            <Text fontSize={18} color="#707070" textAlign="center">
              {CONTENT_CONFIG.maintenance.footer}
            </Text>
          )}
        </BodyContainer>
      </CardContainer>
    );
  };

  return renderContent();
}

export default FbTokenExpired;